import React, { useState } from 'react';
import { Send, User, Mail, Phone, Building2, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const WHATSAPP_NUMBER = "5565992258000";

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    servico: '',
    mensagem: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Monta mensagem para WhatsApp
    const message = `*Solicitação de Orçamento - Tokefrio*%0A%0A*Nome:* ${formData.nome}%0A*E-mail:* ${formData.email}%0A*Telefone:* ${formData.telefone}%0A*Empresa:* ${formData.empresa}%0A*Serviço:* ${formData.servico}%0A*Mensagem:* ${formData.mensagem}`;
    
    // Abre WhatsApp com a mensagem
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    
    setIsSubmitted(true);
    
    // Reset após 3 segundos
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        empresa: '',
        servico: '',
        mensagem: '',
      });
    }, 3000);
  };

  const servicos = [
    'Projetos de climatização',
    'Execução de obras',
    'Instalação de sistemas',
    'Manutenção preventiva',
    'Manutenção corretiva',
    'Contratos PMOC',
  ];

  return (
    <section
      id="orcamento"
      data-testid="contact-form-section"
      className="py-20 lg:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Info */}
          <div data-testid="contact-form-info">
            <span className="font-manrope text-sm font-bold tracking-[0.2em] uppercase text-sky-600">
              Solicitar Orçamento
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 mt-4 mb-6">
              Fale com nossa equipe técnica
            </h2>
            <div className="section-divider mb-6"></div>
            <p className="font-manrope text-base sm:text-lg text-slate-600 leading-relaxed mb-8">
              Preencha o formulário ao lado e nossa equipe entrará em contato para 
              entender suas necessidades e oferecer a melhor solução em climatização 
              para sua empresa.
            </p>

            {/* Benefits */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-outfit text-base font-semibold text-slate-800">Resposta rápida</h3>
                  <p className="font-manrope text-sm text-slate-600">Retornamos em até 24 horas úteis</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-outfit text-base font-semibold text-slate-800">Orçamento sem compromisso</h3>
                  <p className="font-manrope text-sm text-slate-600">Avaliação técnica gratuita</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-outfit text-base font-semibold text-slate-800">Atendimento personalizado</h3>
                  <p className="font-manrope text-sm text-slate-600">Soluções sob medida para sua empresa</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div
            className="bg-white rounded-2xl shadow-xl p-8 lg:p-10 border border-slate-100"
            data-testid="contact-form-container"
          >
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center" data-testid="form-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">
                  Mensagem enviada!
                </h3>
                <p className="font-manrope text-sm text-slate-600">
                  Você será redirecionado para o WhatsApp.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {/* Nome */}
                  <div>
                    <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                      Nome completo *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required
                        placeholder="Seu nome"
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-manrope text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                        data-testid="input-nome"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                      E-mail *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="seu@email.com"
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-manrope text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {/* Telefone */}
                  <div>
                    <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                      Telefone *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        required
                        placeholder="(65) 99999-9999"
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-manrope text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                        data-testid="input-telefone"
                      />
                    </div>
                  </div>

                  {/* Empresa */}
                  <div>
                    <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                      Empresa
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        placeholder="Nome da empresa"
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-manrope text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                        data-testid="input-empresa"
                      />
                    </div>
                  </div>
                </div>

                {/* Serviço */}
                <div className="mb-4">
                  <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                    Serviço de interesse *
                  </label>
                  <select
                    name="servico"
                    value={formData.servico}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-manrope text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                    data-testid="select-servico"
                  >
                    <option value="">Selecione um serviço</option>
                    {servicos.map((servico) => (
                      <option key={servico} value={servico}>
                        {servico}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mensagem */}
                <div className="mb-6">
                  <label className="block font-manrope text-sm font-medium text-slate-700 mb-2">
                    Mensagem
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <textarea
                      name="mensagem"
                      value={formData.mensagem}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Descreva sua necessidade..."
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-manrope text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all resize-none"
                      data-testid="textarea-mensagem"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-full px-8 py-4 text-base transition-all btn-primary"
                  data-testid="submit-button"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Enviar Solicitação
                </Button>

                <p className="font-manrope text-xs text-slate-500 text-center mt-4">
                  Ao enviar, você será direcionado para o WhatsApp para confirmar a mensagem.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
